<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FunnelFieldInput from 'dashboard/components-next/funnels/FunnelFieldInput.vue';

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  funnel: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const values = ref({});
// Map of field key -> field name for the fields the backend rejected as a
// missing required value (from the structured 422). See §9.6.
const fieldErrors = ref({});
const isSaving = ref(false);

const sortedFields = computed(() =>
  [...(props.funnel.fields || [])].sort((a, b) => a.position - b.position)
);

const errorFor = field =>
  fieldErrors.value[field.key]
    ? t('FUNNELS.CARD.EDIT.REQUIRED_ERROR', { name: field.name })
    : '';

const setValue = (field, value) => {
  values.value = { ...values.value, [field.key]: value };
};

const open = () => {
  fieldErrors.value = {};
  values.value = { ...(props.card.custom_field_values || {}) };
  dialogRef.value?.open();
};

const handleConfirm = async () => {
  isSaving.value = true;
  fieldErrors.value = {};
  try {
    await store.dispatch('funnels/updateCardValues', {
      funnelId: props.funnel.id,
      cardId: props.card.id,
      customFieldValues: values.value,
    });
    useAlert(t('FUNNELS.CARD.EDIT.SUCCESS'));
    dialogRef.value?.close();
  } catch (error) {
    const errors = error?.response?.data?.errors;
    if (error?.response?.status === 422 && Array.isArray(errors)) {
      // Structured 422: mark each missing field so its message renders inline.
      fieldErrors.value = errors.reduce(
        (acc, item) => ({ ...acc, [item.key]: item.name }),
        {}
      );
    } else {
      useAlert(t('FUNNELS.CARD.EDIT.ERROR'));
    }
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('FUNNELS.CARD.EDIT.TITLE')"
    :confirm-button-label="t('FUNNELS.CARD.EDIT.SAVE')"
    :is-loading="isSaving"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <p v-if="!sortedFields.length" class="text-sm text-n-slate-11">
        {{ t('FUNNELS.CARD.EDIT.EMPTY') }}
      </p>

      <FunnelFieldInput
        v-for="field in sortedFields"
        :key="field.id"
        :field="field"
        :model-value="values[field.key] ?? ''"
        :error="errorFor(field)"
        @update:model-value="value => setValue(field, value)"
      />
    </div>
  </Dialog>
</template>
